import { useEffect, useState } from 'react'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { useTranslation } from '../contexts/TranslationContext'
import { ui } from '../styles/ui'
import productCategorizationExclusionService from '../services/productCategorizationExclusionService'
import { formatDateTime } from '../utils/date'

export default function QrExclusionPhrasesAdmin() {
  const [exclusionPhrases, setExclusionPhrases] = useState([])
  const [exclusionLoading, setExclusionLoading] = useState(false)
  const [exclusionSaving, setExclusionSaving] = useState(false)
  const [exclusionForm, setExclusionForm] = useState('')
  const [pendingDeleteExclusion, setPendingDeleteExclusion] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    loadExclusions()
  }, [])

  async function loadExclusions() {
    setExclusionLoading(true)
    try {
      const res = await productCategorizationExclusionService.list(1, 100)
      const list = res?.data?.items || res?.data?.data || []
      setExclusionPhrases(Array.isArray(list) ? list : [])
    } catch (error) {
      console.error('Failed to load exclusion phrases', error)
      setExclusionPhrases([])
    } finally {
      setExclusionLoading(false)
    }
  }

  async function addPhraseToExclusions(phrase, source = 'manual') {
    const value = String(phrase || '').trim()
    if (!value) return

    setExclusionSaving(true)
    try {
      await productCategorizationExclusionService.create({
        phrase: value,
        reason: 'manual',
        source,
      })
      setExclusionForm('')
      await loadExclusions()
    } catch (error) {
      console.error('Failed to add exclusion phrase', error)
    } finally {
      setExclusionSaving(false)
    }
  }

  async function submitExclusionForm(event) {
    event.preventDefault()
    await addPhraseToExclusions(exclusionForm, 'manual')
  }

  async function removeExclusionPhrase() {
    if (!pendingDeleteExclusion) return
    try {
      await productCategorizationExclusionService.remove(pendingDeleteExclusion.id)
      setPendingDeleteExclusion(null)
      await loadExclusions()
    } catch (error) {
      console.error('Failed to remove exclusion phrase', error)
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={t('QR Exclusion Phrases', 'QR Exclusion Phrases')}
      />

      <Card>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="text-xl font-semibold text-foreground">{t('Exclusion phrases', 'Exclusion phrases')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('These phrases live in the database and are used to drop noisy QR lines before they reach receipt items.', 'These phrases live in the database and are used to drop noisy QR lines before they reach receipt items.')}
            </p>
          </div>
          <form className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto" onSubmit={submitExclusionForm}>
            <input
              className={`${ui.input} sm:min-w-80`}
              value={exclusionForm}
              onChange={(event) => setExclusionForm(event.target.value)}
              placeholder={t('Add a phrase', 'Add a phrase')}
            />
            <button
              type="submit"
              className={ui.buttonSmPrimary}
              disabled={exclusionSaving || !exclusionForm.trim()}
            >
              {exclusionSaving ? t('Adding...', 'Adding...') : t('Add phrase', 'Add phrase')}
            </button>
          </form>
        </div>

        <div className="mt-4">
          {exclusionLoading ? (
            <div className="flex items-center justify-center py-8"><div className={ui.spinner} /></div>
          ) : (
            <table className={ui.table}>
              <thead className={ui.tableHead}>
                <tr>
                  <th className={ui.tableHeadCell}>{t('Phrase', 'Phrase')}</th>
                  <th className={ui.tableHeadCell}>{t('Reason', 'Reason')}</th>
                  <th className={ui.tableHeadCell}>{t('Source', 'Source')}</th>
                  <th className={ui.tableHeadCell}>{t('Created', 'Created')}</th>
                  <th className={ui.tableHeadCell}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {exclusionPhrases.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-muted-foreground">
                      {t('No exclusion phrases found', 'No exclusion phrases found')}
                    </td>
                  </tr>
                ) : exclusionPhrases.map((phrase) => (
                  <tr key={phrase.id} className={ui.tableRow}>
                    <td className={`${ui.tableCellText} align-top whitespace-normal break-words`}>
                      <div className="font-medium text-foreground">{phrase.phrase}</div>
                      <div className="mt-1 text-xs text-muted-foreground break-all">{phrase.normalizedPhrase}</div>
                    </td>
                    <td className={`${ui.tableCellText} align-top`}>
                      <span className={ui.badgeNeutral}>{phrase.reason}</span>
                    </td>
                    <td className={`${ui.tableCellText} align-top`}>
                      <span className={ui.badgeNeutral}>{phrase.source}</span>
                    </td>
                    <td className={`${ui.tableCellText} align-top`}>{formatDateTime(phrase.createdAt)}</td>
                    <td className={`${ui.tableCell} align-top`}>
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="danger" onClick={() => setPendingDeleteExclusion(phrase)}>
                          {t('Delete', 'Delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <ConfirmDialog
        open={Boolean(pendingDeleteExclusion)}
        onClose={() => setPendingDeleteExclusion(null)}
        onConfirm={removeExclusionPhrase}
        title={t('Delete exclusion phrase', 'Delete exclusion phrase')}
      >
        <div className="space-y-2">
          <p>{t('Are you sure you want to delete this exclusion phrase?', 'Are you sure you want to delete this exclusion phrase?')}</p>
          {pendingDeleteExclusion ? (
            <p className="text-sm text-muted-foreground break-words">
              {pendingDeleteExclusion.phrase}
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </PageShell>
  )
}
